import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, X, Loader2, UserPlus, Users, UserMinus, ChevronDown, ChevronRight,
} from 'lucide-react';
import {
  getPersonasElegibles, getNegocios, getDepartamentos, getEmpresasColaboradoras,
} from '../api';
import { AreaFilter } from '../../ui-v2';
import { cn } from '../../../../lib/cn';

/**
 * SelectorPersonas — Fase 16c: filtros MULTI-VALOR.
 *
 * Filtros por ámbito:
 *   - Interno:     [buscador] [negocio (chips multi)] [departamento (multi buscable)] (+ desglose)
 *   - Colaborador: [buscador] [empresa colaboradora (chips multi)]
 *
 * Los filtros se envían al backend como CSV (?negocio_id=1,4). Retrocompatible.
 *
 * Zona "Seleccionados": agrupada por negocio (colaborador: por empresa) con
 * acordeón inline. "Añadir todas" / "Quitar las del bloque" respetan los
 * filtros activos.
 */
export default function SelectorPersonas({
  rol,
  ambito = 'interno',
  excluirIds = [],
  seleccionados,
  cache,
  onAdd,
  onRemove,
  errores,
  // Fuera del asistente (lote manual) no hay «paso 1» y el ámbito no se cambia.
  enAsistente = true,
  // Lotes: con la campaña, el servidor quita de la consulta a quien ya está en ella
  // (excluir_en_campana=1); se pueden «Mostrar» en gris, no seleccionables, con su lote.
  // Sin campanaId (el asistente) nada cambia.
  campanaId = null,
  // Rediseño (Fase 2, Editar): seleccionados a la izquierda y «Añadir personas» a la derecha.
  // Sin él, una columna como siempre (asistente y modales de lotes).
  dosColumnas = false,
}) {
  const esColaborador = ambito === 'colaborador';
  const [mostrarInscritas, setMostrarInscritas] = useState(false);
  const [enCampanaDelFiltro, setEnCampanaDelFiltro] = useState(null);
  // Parámetros de campaña para /personas-elegibles. `excluir`: quitar a las inscritas.
  const paramsCampana = useCallback(
    (excluir) => (campanaId ? { campana_id: campanaId, ...(excluir ? { excluir_en_campana: 1 } : {}) } : {}),
    [campanaId]
  );

  const [q, setQ] = useState('');
  const debouncedQ = useDebouncedValue(q, 300);

  // Internos (multi-valor)
  const [negocios, setNegocios] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [desglose, setDesglose] = useState(false);
  const [negocioIds, setNegocioIds] = useState([]);
  const [deptoValues, setDeptoValues] = useState([]);

  // Colaboradores (multi-valor)
  const [empresasColab, setEmpresasColab] = useState([]);
  const [empresaColabIds, setEmpresaColabIds] = useState([]);

  const [resultados, setResultados] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [agregandoBloque, setAgregandoBloque] = useState(false);
  const [confirmandoQuitarTodos, setConfirmandoQuitarTodos] = useState(false);
  const [busquedaGlobal, setBusquedaGlobal] = useState(''); // #19 · buscar en Seleccionados

  // Cargar catálogos según ámbito
  useEffect(() => {
    if (esColaborador) {
      setNegocios([]); setDepartamentos([]);
      setNegocioIds([]); setDeptoValues([]);
      getEmpresasColaboradoras().then(setEmpresasColab).catch(() => setEmpresasColab([]));
    } else {
      setEmpresasColab([]);
      setEmpresaColabIds([]);
      getNegocios().then(setNegocios).catch(() => setNegocios([]));
    }
  }, [esColaborador]);

  // Departamentos: filtrados por negocio solo si hay EXACTAMENTE uno seleccionado
  // (con varios/ninguno se cargan todos; el multiselect es buscable). Solo internos.
  useEffect(() => {
    if (esColaborador) return;
    const params = {};
    if (negocioIds.length === 1) params.negocio_id = negocioIds[0];
    if (desglose) params.desglose = 1;
    getDepartamentos(params).then(setDepartamentos).catch(() => setDepartamentos([]));
    setDeptoValues([]);
  }, [negocioIds, desglose, esColaborador]);

  // Reset filtros al cambiar ámbito
  useEffect(() => {
    setQ('');
    setNegocioIds([]); setDeptoValues([]); setDesglose(false);
    setEmpresaColabIds([]);
  }, [ambito]);

  const buscar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { page: 1, limit: 30, ambito };
      if (debouncedQ) params.q = debouncedQ;
      if (esColaborador) {
        if (empresaColabIds.length) params.empresa_colaboradora_id = empresaColabIds.join(',');
      } else {
        if (negocioIds.length) params.negocio_id = negocioIds.join(',');
        if (deptoValues.length) {
          if (desglose) params.departamento_id = deptoValues.join(',');
          else params.departamento_nombre = deptoValues.join(',');
        }
      }
      Object.assign(params, paramsCampana(!mostrarInscritas));
      const res = await getPersonasElegibles(params);
      setResultados(res.data || []);
      setTotal(res.total || 0);
      setEnCampanaDelFiltro(res.en_campana_del_filtro ?? null);
    } catch (e) {
      console.error('[SelectorPersonas]', e);
      setError(e?.response?.data?.error || 'Error al cargar personas.');
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, negocioIds, deptoValues, desglose, ambito, esColaborador, empresaColabIds, paramsCampana, mostrarInscritas]);

  useEffect(() => { buscar(); }, [buscar]);

  const seleccionadosArr = seleccionados.map((id) => cache[id]).filter(Boolean);
  const excluirSet = new Set([...excluirIds, ...seleccionados]);
  const resultadosFiltrados = resultados.filter((r) => !excluirSet.has(r.id));
  // Las que se pueden añadir del filtro: si se están mostrando las ya inscritas, no cuentan.
  const totalAnadible = mostrarInscritas && enCampanaDelFiltro ? Math.max(0, total - enCampanaDelFiltro) : total;

  const errorRol = errores.find(
    (e) => e.campo === (rol === 'tecnico' ? 'tecnicos_ids' : 'usuarios_ids')
  )?.mensaje;

  // --- Toggles de filtros multi ---
  const toggle = (setter) => (val) =>
    setter((prev) => (prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]));
  const toggleNegocio = toggle(setNegocioIds);
  const toggleEmpresaColab = toggle(setEmpresaColabIds);
  const toggleDepto = toggle(setDeptoValues);

  // Opciones del multiselect de departamento (según modo desglose)
  const departamentoOptions = useMemo(() => (
    desglose
      ? departamentos.map((d) => ({
          value: String(d.id),
          label: `${d.nombre}${d.negocio_nombre ? ` · ${d.negocio_nombre}` : ''}`,
        }))
      : departamentos.map((d) => ({ value: d.nombre, label: d.nombre }))
  ), [departamentos, desglose]);

  // --- Filtro activo (cualquier ámbito) ---
  const hayFiltroBloque = esColaborador
    ? empresaColabIds.length > 0
    : (negocioIds.length > 0 || deptoValues.length > 0);

  // --- Añadir en bloque ---
  async function anyadirBloque() {
    setAgregandoBloque(true);
    try {
      const params = { ambito, page: 1, limit: 500 };
      if (esColaborador) {
        if (empresaColabIds.length) params.empresa_colaboradora_id = empresaColabIds.join(',');
      } else {
        if (negocioIds.length) params.negocio_id = negocioIds.join(',');
        if (deptoValues.length) {
          if (desglose) params.departamento_id = deptoValues.join(',');
          else params.departamento_nombre = deptoValues.join(',');
        }
      }
      // «Añadir todas» nunca trae a quien ya está en la campaña, se estén mostrando o no.
      Object.assign(params, paramsCampana(true));
      const res = await getPersonasElegibles(params);
      const lista = (res.data || []).filter((p) => !excluirSet.has(p.id) && !p.en_campana);
      for (const p of lista) onAdd(p);
    } catch (e) {
      console.error('[anyadirBloque]', e);
      setError(e?.response?.data?.error || 'No se pudo añadir el bloque.');
    } finally {
      setAgregandoBloque(false);
    }
  }

  // --- Quitar todos ---
  function pedirQuitarTodos() {
    if (seleccionadosArr.length === 0) return;
    if (seleccionadosArr.length <= 5) quitarTodos();
    else setConfirmandoQuitarTodos(true);
  }
  function quitarTodos() {
    for (const p of seleccionadosArr) onRemove(p.id);
    setConfirmandoQuitarTodos(false);
  }

  // --- Quitar los del filtro activo (multi) ---
  const seleccionadosDelFiltro = useMemo(() => {
    if (!hayFiltroBloque) return [];
    if (esColaborador) {
      const set = new Set(empresaColabIds.map(String));
      return seleccionadosArr.filter((p) => set.has(String(p.empresa_colaboradora_id)));
    }
    const negSet = new Set(negocioIds.map(String));
    const depSet = new Set(deptoValues.map(String));
    return seleccionadosArr.filter((p) => {
      const okNeg = negocioIds.length === 0 || negSet.has(String(p.negocio_id));
      const okDep = deptoValues.length === 0
        || (desglose ? depSet.has(String(p.departamento_id)) : depSet.has(p.departamento));
      return okNeg && okDep;
    });
  }, [hayFiltroBloque, esColaborador, empresaColabIds, negocioIds, deptoValues, desglose, seleccionadosArr]);

  function quitarDelFiltro() {
    for (const p of seleccionadosDelFiltro) onRemove(p.id);
  }

  // --- Quitar un grupo entero (zona seleccionados) ---
  function quitarGrupo(ids) {
    if (ids.length >= 10 && !window.confirm(`¿Quitar ${ids.length} personas de este grupo?`)) return;
    for (const id of ids) onRemove(id);
  }

  // --- Agrupar seleccionados por negocio (colaborador: por empresa) ---
  const gruposSeleccionados = useMemo(() => {
    const map = new Map();
    for (const p of seleccionadosArr) {
      const key = esColaborador
        ? (p.empresa_colaboradora || 'Sin empresa')
        : (p.negocio || 'Sin negocio');
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [seleccionadosArr, esColaborador]);

  // #19 · buscador global sobre Seleccionados. Devuelve [titulo, todas, visibles];
  // oculta grupos sin matches y (vía forzarAbierto) auto-expande los que sí.
  const busquedaGlobalNorm = busquedaGlobal.trim().toLowerCase();
  const gruposParaMostrar = useMemo(() => {
    if (!busquedaGlobalNorm) return gruposSeleccionados.map(([g, ps]) => [g, ps, ps]);
    return gruposSeleccionados
      .map(([g, ps]) => {
        const visibles = ps.filter((p) => {
          const nombre = (p.nombre_completo || '').toLowerCase();
          const email = (p.email || '').toLowerCase();
          return nombre.includes(busquedaGlobalNorm) || email.includes(busquedaGlobalNorm);
        });
        return [g, ps, visibles];
      })
      .filter(([, , visibles]) => visibles.length > 0);
  }, [gruposSeleccionados, busquedaGlobalNorm]);
  const totalEncontrados = gruposParaMostrar.reduce((n, [, , v]) => n + v.length, 0);

  const etiquetaBloque = (() => {
    if (esColaborador) {
      return empresaColabIds.length ? `de ${empresaColabIds.length} empresa(s)` : '';
    }
    const partes = [];
    if (negocioIds.length) partes.push(`${negocioIds.length} negocio(s)`);
    if (deptoValues.length) partes.push(`${deptoValues.length} departamento(s)`);
    return partes.length ? `de ${partes.join(' y ')}` : '';
  })();

  return (
    <div className={dosColumnas ? 'grid gap-x-6 gap-y-4 lg:grid-cols-2 lg:items-start' : 'space-y-4 max-w-4xl'}>
      {errorRol && (
        <div className={'text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2' + (dosColumnas ? ' lg:col-span-2' : '')}>
          {errorRol}
        </div>
      )}

      <div className={'text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-md px-3 py-2' + (dosColumnas ? ' lg:col-span-2' : '')}>
        Mostrando personas del ámbito{' '}
        <strong className="text-slate-700">
          {esColaborador ? 'Colaboradores' : 'Internos'}
        </strong>
        .{enAsistente ? ' Puedes cambiarlo en el paso 1.' : ' Es el de la campaña.'}
      </div>

      {/* Seleccionados (agrupados por negocio) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-slate-700">
            Seleccionados ({seleccionadosArr.length})
            {busquedaGlobalNorm && (
              <span className="text-slate-400 font-normal"> · {totalEncontrados} encontrado{totalEncontrados === 1 ? '' : 's'}</span>
            )}
          </p>
          {seleccionadosArr.length > 0 && (
            <button
              type="button"
              onClick={pedirQuitarTodos}
              className="text-xs text-slate-500 hover:text-red-600 flex items-center gap-1"
              title="Quitar a todas las personas de la lista"
            >
              <UserMinus className="w-3.5 h-3.5" />
              Quitar todos
            </button>
          )}
        </div>

        {/* #19 · buscador global (solo si aporta: >3 seleccionados) */}
        {seleccionadosArr.length > 3 && (
          <div className="relative mb-2">
            <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={busquedaGlobal}
              onChange={(e) => setBusquedaGlobal(e.target.value)}
              placeholder="Buscar en seleccionados…"
              className="w-full text-xs border border-slate-200 rounded-md pl-7 pr-7 py-1.5 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            />
            {busquedaGlobal && (
              <button
                type="button"
                onClick={() => setBusquedaGlobal('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Limpiar búsqueda"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        )}

        {seleccionadosArr.length === 0 ? (
          <div className="border border-dashed border-slate-200 rounded-lg p-4 text-center text-xs text-slate-400">
            Nadie seleccionado todavía.
          </div>
        ) : gruposParaMostrar.length === 0 ? (
          <div className="border border-dashed border-slate-200 rounded-lg p-4 text-center text-xs text-slate-400">
            Sin resultados para "{busquedaGlobal}".
          </div>
        ) : (
          <div className="space-y-1.5">
            {gruposParaMostrar.map(([grupo, personas, visibles]) => (
              <GrupoSeleccionados
                key={grupo}
                titulo={grupo}
                personas={personas}
                personasVisibles={visibles}
                forzarAbierto={!!busquedaGlobalNorm}
                onQuitarPersona={onRemove}
                onQuitarGrupo={() => quitarGrupo(personas.map((p) => p.id))}
              />
            ))}
          </div>
        )}
      </div>

      <div className={dosColumnas ? 'space-y-3 lg:border-l lg:border-slate-100 lg:pl-6' : 'border-t border-slate-100 pt-4 space-y-3'}>
        <p className="text-xs font-medium text-slate-700">Añadir personas</p>

        {/* Buscador */}
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
          <input
            type="text"
            placeholder="Buscar por nombre o email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
          />
        </div>

        {/* Filtros multi-valor */}
        {esColaborador ? (
          empresasColab.length > 0 && (
            <FiltroChips
              label="Empresa colaboradora"
              opciones={empresasColab}
              seleccionados={empresaColabIds}
              onToggle={toggleEmpresaColab}
              onClear={() => setEmpresaColabIds([])}
            />
          )
        ) : (
          <div className="space-y-3">
            {negocios.length > 0 && (
              <FiltroChips
                label="Negocio"
                opciones={negocios}
                seleccionados={negocioIds}
                onToggle={toggleNegocio}
                onClear={() => setNegocioIds([])}
              />
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-start">
              <MultiSelectBuscable
                label="Departamento"
                options={departamentoOptions}
                selected={deptoValues}
                onToggle={toggleDepto}
                onClear={() => setDeptoValues([])}
                disabled={departamentoOptions.length === 0}
              />
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none md:pt-7">
                <input
                  type="checkbox"
                  checked={desglose}
                  onChange={(e) => setDesglose(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-slate-300"
                />
                Ver desglose por unidad de negocio
                <span className="text-slate-400">(separa homónimos)</span>
              </label>
            </div>
          </div>
        )}

        {/* Barra de acciones por bloque */}
        {hayFiltroBloque && (totalAnadible > 0 || seleccionadosDelFiltro.length > 0) && (
          <div className="flex items-center justify-between gap-3 px-3 py-2 bg-slate-50 border border-slate-100 rounded-md flex-wrap">
            <div className="text-xs text-slate-600 min-w-0">
              <Users className="w-3.5 h-3.5 inline-block mr-1 text-slate-400" />
              {totalAnadible} {totalAnadible === 1 ? 'persona' : 'personas'} {etiquetaBloque}
              {seleccionadosDelFiltro.length > 0 && (
                <span className="text-slate-400">
                  {' · '}{seleccionadosDelFiltro.length} ya en la lista
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {seleccionadosDelFiltro.length > 0 && (
                <button
                  type="button"
                  onClick={quitarDelFiltro}
                  className="text-xs font-medium text-red-600 hover:text-red-700 flex items-center gap-1"
                  title={`Quitar las ${seleccionadosDelFiltro.length} personas de este bloque`}
                >
                  <UserMinus className="w-3.5 h-3.5" />
                  Quitar las del bloque
                </button>
              )}
              {totalAnadible > 0 && (
                <button
                  type="button"
                  onClick={anyadirBloque}
                  disabled={agregandoBloque}
                  className="text-xs font-medium text-slate-700 hover:text-slate-900 flex items-center gap-1 disabled:opacity-50"
                >
                  {agregandoBloque ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <UserPlus className="w-3.5 h-3.5" />
                  )}
                  Añadir todas
                </button>
              )}
            </div>
          </div>
        )}

        {/* Lotes: las que ya están en la campaña, fuera por defecto; se pueden mostrar en gris. */}
        {campanaId && enCampanaDelFiltro > 0 && (
          <div className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-md px-3 py-2">
            {enCampanaDelFiltro} {enCampanaDelFiltro === 1 ? 'persona de este filtro ya está' : 'personas de este filtro ya están'} en la campaña
            {' · '}
            <button
              type="button"
              onClick={() => setMostrarInscritas((v) => !v)}
              className="underline text-slate-800 hover:text-primary"
            >
              {mostrarInscritas ? 'Ocultarlas' : 'Mostrarlas'}
            </button>
          </div>
        )}

        {/* Resultados */}
        <div className="border border-slate-200 rounded-lg max-h-72 overflow-y-auto">
          {loading ? (
            <div className="py-8 text-center text-sm text-slate-500">
              <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" />
              Buscando…
            </div>
          ) : error ? (
            <div className="py-8 text-center text-sm text-red-600">{error}</div>
          ) : resultadosFiltrados.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">
              {total === 0 && enCampanaDelFiltro > 0
                ? 'Todas las personas de este filtro ya están en la campaña.'
                : total === 0
                  ? `No hay ${esColaborador ? 'colaboradores' : 'personas internas'} que coincidan con los filtros.`
                  : 'Todas las personas que coinciden ya están seleccionadas.'}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {resultadosFiltrados.map((p) => (p.en_campana ? (
                // Ya está en la campaña: a la vista, pero no seleccionable.
                <li key={p.id} className="px-3 py-2.5 flex items-center justify-between gap-3 bg-slate-50/60">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-400 truncate">{p.nombre_completo}</div>
                    <div className="text-xs text-slate-400 truncate">
                      Ya en la campaña{p.en_campana.lote ? ` · Lote ${p.en_campana.lote.numero} · ${p.en_campana.lote.nombre}` : ''}
                    </div>
                  </div>
                </li>
              ) : (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => onAdd(p)}
                    className="w-full px-3 py-2.5 flex items-center justify-between gap-3 hover:bg-slate-50 text-left"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-800 truncate">
                        {p.nombre_completo}
                      </div>
                      <div className="text-xs text-slate-400 truncate">
                        {esColaborador
                          ? ([p.empresa_colaboradora, p.email].filter(Boolean).join(' · ') || '—')
                          : ([p.departamento, p.negocio].filter(Boolean).join(' · ') || p.email || '—')}
                      </div>
                    </div>
                    <UserPlus className="w-4 h-4 text-slate-400 shrink-0" />
                  </button>
                </li>
              )))}
            </ul>
          )}
        </div>
        {!loading && resultados.length > 0 && total > resultados.length && (
          <p className="text-[11px] text-slate-400">
            Mostrando {resultados.length} de {total}. Afina la búsqueda para ver más
            o usa "Añadir todas" si has aplicado un filtro.
          </p>
        )}
      </div>

      {/* Confirmación de quitar todos */}
      {confirmandoQuitarTodos && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-50 grid place-items-center px-4"
          onClick={() => setConfirmandoQuitarTodos(false)}
        >
          <div
            className="bg-white rounded-lg shadow-lg max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-slate-900 mb-2">
              Quitar a todas las personas
            </h3>
            <p className="text-sm text-slate-600">
              Vas a vaciar la lista de seleccionados ({seleccionadosArr.length}{' '}
              {seleccionadosArr.length === 1 ? 'persona' : 'personas'}). Tendrás
              que volver a añadirlas si te equivocas.
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmandoQuitarTodos(false)}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={quitarTodos}
                className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                Sí, quitar todos
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Filtro de chips multi (patrón AreaFilter del DS) ──────────────
function FiltroChips({ label, opciones, seleccionados, onToggle, onClear }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1.5">
        {label} {seleccionados.length > 0 && `(${seleccionados.length})`}
      </label>
      <div className="flex flex-wrap gap-1.5">
        {opciones.map((o) => (
          <AreaFilter
            key={o.id}
            active={seleccionados.includes(o.id)}
            onClick={() => onToggle(o.id)}
          >
            {o.nombre}
          </AreaFilter>
        ))}
        {seleccionados.length > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs text-slate-500 hover:text-slate-900 px-2 py-1"
          >
            Limpiar
          </button>
        )}
      </div>
    </div>
  );
}

// ── Multiselect buscable (departamento: muchas opciones) ──────────
function MultiSelectBuscable({ label, options, selected, onToggle, onClear, disabled }) {
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState('');
  const filtradas = q
    ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
    : options;

  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1.5">
        {label} {selected.length > 0 && `(${selected.length})`}
      </label>
      <div className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setAbierto((a) => !a)}
          className={cn(
            'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-left flex items-center justify-between',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          <span className={selected.length ? 'text-slate-900' : 'text-slate-400'}>
            {selected.length === 0 ? 'Todos los departamentos' : `${selected.length} seleccionado(s)`}
          </span>
          <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
        </button>

        {abierto && !disabled && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setAbierto(false)} />
            <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              <div className="p-2 sticky top-0 bg-white border-b border-slate-100">
                <input
                  type="text"
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar departamento…"
                  className="w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                />
              </div>
              {filtradas.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-slate-400">Sin resultados</div>
              ) : (
                filtradas.map((o) => (
                  <label
                    key={o.value}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-sm text-slate-700"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(o.value)}
                      onChange={() => onToggle(o.value)}
                      className="w-3.5 h-3.5 rounded border-slate-300"
                    />
                    <span className="truncate">{o.label}</span>
                  </label>
                ))
              )}
              {selected.length > 0 && (
                <button
                  type="button"
                  onClick={onClear}
                  className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 border-t border-slate-100"
                >
                  Limpiar selección ({selected.length})
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Grupo de seleccionados (acordeón inline) ──────────────────────
// #19 · personasVisibles = subconjunto tras la búsqueda global (si la hay);
// forzarAbierto = true cuando hay búsqueda global (auto-expande los que matchean);
// buscadorGrupo local (independiente) se aplica en AND sobre personasVisibles.
function GrupoSeleccionados({ titulo, personas, personasVisibles, forzarAbierto, onQuitarPersona, onQuitarGrupo }) {
  const [abierto, setAbierto] = useState(true);
  const [busquedaGrupo, setBusquedaGrupo] = useState('');

  const efectivamenteAbierto = forzarAbierto || abierto;
  const base = personasVisibles || personas;

  const personasFinal = useMemo(() => {
    const q = busquedaGrupo.trim().toLowerCase();
    if (!q) return base;
    return base.filter((p) => {
      const nombre = (p.nombre_completo || '').toLowerCase();
      const email = (p.email || '').toLowerCase();
      return nombre.includes(q) || email.includes(q);
    });
  }, [base, busquedaGrupo]);

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden">
      <div className="w-full px-3 py-2 flex items-center justify-between hover:bg-slate-50">
        <button
          type="button"
          onClick={() => setAbierto((a) => !a)}
          className="flex items-center gap-2 min-w-0 flex-1 text-left"
        >
          {efectivamenteAbierto
            ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
          <span className="text-sm font-medium text-slate-900 truncate">{titulo}</span>
          <span className="text-xs text-slate-500 shrink-0">({base.length})</span>
        </button>
        <button
          type="button"
          onClick={onQuitarGrupo}
          className="text-[11px] text-slate-500 hover:text-red-600 px-2 py-0.5 shrink-0"
        >
          Quitar todos
        </button>
      </div>

      {efectivamenteAbierto && (
        <div className="px-3 py-2 border-t border-slate-100 space-y-1">
          {/* #19 · buscador local (solo si el grupo tiene >5 personas) */}
          {personas.length > 5 && (
            <input
              type="text"
              value={busquedaGrupo}
              onChange={(e) => setBusquedaGrupo(e.target.value)}
              placeholder={`Buscar en ${titulo}…`}
              className="w-full text-xs border border-slate-200 rounded-md px-2 py-1 mb-1 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            />
          )}
          {personasFinal.map((p) => (
            <div key={p.id} className="flex items-center justify-between text-xs">
              <span className="text-slate-700 truncate">
                {p.nombre_completo || `Persona ${p.id}`}
              </span>
              <button
                type="button"
                onClick={() => onQuitarPersona(p.id)}
                className="text-slate-400 hover:text-red-600 p-0.5 shrink-0"
                title="Quitar"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {personasFinal.length === 0 && (
            <div className="text-xs text-slate-400 italic py-1">Sin resultados.</div>
          )}
        </div>
      )}
    </div>
  );
}

function useDebouncedValue(value, ms) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return d;
}
